/**
 * entity_write - Universal Write Command for Notes and Sheets
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { StandardToolResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { getApplicationForEntity } from '../../services/applicationRegistry';

/**
 * Extract readable text content from content/attrs parameters
 */
function extractContentFromParams(content: any, attrs: any, operation: string): string | null {
  // For delete operations, no content to extract
  if (operation === 'delete') {
    return null;
  }
  
  // Extract from structured content object (Notes, Canvas, Presentations, Playbooks)
  if (content && typeof content === 'object' && !Array.isArray(content)) {
    // Priority 1: attrs.text (universal pattern for all text including playbooks)
    if (content.attrs?.text) {
      return content.attrs.text;
    }
    // Priority 2: Legacy content field
    if (content.content && typeof content.content === 'string') {
      return content.content;
    }
    // For non-text elements (shapes, images), return a description
    if (content.className) {
      const className = content.className;
      if (className.includes('shape') || className.includes('image')) {
        return `[${className}]`;
      }
    }
  }
  
  // Extract from array of content objects
  if (Array.isArray(content)) {
    const texts = content
      .map((item: any) => item.attrs?.text || item.content)
      .filter(Boolean);
    if (texts.length > 0) {
      return texts.join('\n\n');
    }
  }
  
  // Extract from plain string (legacy support)
  if (typeof content === 'string') {
    return content;
  }
  
  // Extract from attrs (Sheets operations)
  if (attrs && typeof attrs === 'object') {
    // For Sheets, show cell references
    const cellRefs = Object.keys(attrs);
    if (cellRefs.length > 0) {
      return `Updated ${cellRefs.length} cell${cellRefs.length > 1 ? 's' : ''}: ${cellRefs.slice(0, 5).join(', ')}${cellRefs.length > 5 ? '...' : ''}`;
    }
  }
  
  return null;
}

/**
 * Generate user-facing contextual message
 */
function generateContextualMessage(operation: string, entityId: string, content: string | null): string {
  // Get entity type from ID
  const entityType = entityId.split('_')[0];
  const entityTypeLabel = entityType === 'note' ? 'note' : 
                         entityType === 'sheet' ? 'sheet' : 
                         entityType === 'canvas' ? 'canvas' :
                         entityType === 'presentation' ? 'presentation' :
                         entityType === 'playbook' ? 'playbook' : 'entity';
  
  // Generate message based on operation
  switch (operation) {
    case 'insert':
      if (content && content.length > 0) {
        const preview = content.length > 50 ? content.substring(0, 50) + '...' : content;
        return `Inserted content in ${entityTypeLabel}`;
      }
      return `Inserted content in ${entityTypeLabel}`;
      
    case 'update':
      return `Updated ${entityTypeLabel}`;
      
    case 'replace':
      return `Replaced content in ${entityTypeLabel}`;
      
    case 'delete':
      return `Deleted content from ${entityTypeLabel}`;
      
    case 'batch':
      return `Applied batch operations to ${entityTypeLabel}`;
      
    default:
      return `Modified ${entityTypeLabel}`;
  }
}

export const entity_write: SimpleDocCommand = {
  id: 'entity_write',
  logicalName: 'entity_write',
  version: 'v2',
  
  description: `⚠️ DEPRECATED: This tool is being replaced by specialized tools with better guidance!

**USE THESE SPECIALIZED TOOLS INSTEAD:**
- note_write for Notes (note_*)
- sheet_write for Sheets (sheet_*)
- canvas_write for Canvas (canvas_*)
- slide_write for Presentations (presentation_*)
- playbook_write for Playbooks (playbook_*)

**WHY SWITCH?**
The specialized tools provide:
- Clearer, focused documentation (100-300 lines vs 694 lines)
- Use-case specific design guidance (Canvas: diagramming, drawing; Slides: beautiful templates)
- Better validation and error messages
- Easier to use and understand

**BACKWARD COMPATIBILITY:**
This tool still works and will continue to work for existing code, but new projects should use the specialized tools.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📋 MIGRATION GUIDE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

OLD (entity_write):
{
  "tool": "entity_write",
  "entityId": "note_abc",
  "operation": "insert",
  "content": { "className": "note-paragraph", "attrs": { "text": "Hello" } }
}

NEW (note_write):
{
  "tool": "note_write",
  "entityId": "note_abc",
  "operation": "insert",
  "content": { "className": "note-paragraph", "attrs": { "text": "Hello" } }
}

The parameters are the same! Just change the tool name.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Insert, update, replace, or delete content in Notes, Sheets, Canvas, Presentations, and Playbooks.

**SUPPORTED ENTITIES:**
- Notes (note_*)
- Sheets (sheet_*)
- Canvas (canvas_*)
- Presentations (presentation_*)
- Playbooks (playbook_*)
- Use doc_write for Documents (doc_*)


**OPERATIONS:**
- insert: Add new content
- update: Modify existing content
- delete: Remove content
- replace: Swap content (Notes only)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚡ CONTENT FORMAT - Structured Objects with className
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🔑 UNIVERSAL RULE: ALL text content uses attrs.text
────────────────────────────────────────────────────────────
For any element with text (Notes, Canvas text, Presentation text):
ALWAYS use: attrs: { text: "your content" }

📝 NOTES → Text Content
────────────────────────────────────────────────────────────
content: { className: "note-paragraph", attrs: { text: "your text here" } }

Example:
{ "entityId": "note_abc", "operation": "insert", "target": {"at": "end"}, "content": { "className": "note-paragraph", "attrs": { "text": "# Summary\\n\\nMeeting notes here" } } }

⚡ PLAYBOOKS → Text Content (System Prompts)
────────────────────────────────────────────────────────────
content: { className: "playbook-prompt", attrs: { text: "your playbook instructions as markdown text" } }

Example:
{ "entityId": "playbook_abc", "operation": "replace", "content": { "className": "playbook-prompt", "attrs": { "text": "# M&A Deal Analysis\\n\\nYou are an M&A analyst..." } } }

📊 SHEETS → Use attrs Parameter (NOT content)
────────────────────────────────────────────────────────────
attrs: { "A1": "value", "B2": "value" }

Example:
{ "entityId": "sheet_data", "operation": "update", "attrs": { "A1": "Name", "B1": "Age", "A2": "Alice" } }

🎨 CANVAS → Shapes and Visual Elements
────────────────────────────────────────────────────────────
Shapes: { className: "shape-rectangle", attrs: { position: {...}, style: {...} } }
Lines/Arrows: { className: "shape-line", attrs: { position: {...}, semantic: { x1, y1, x2, y2 }, style: {...} } }
Text: { className: "canvas-text", attrs: { position: {...}, text: "...", style: {...} } }

Common className: shape-rectangle, shape-circle, shape-line, shape-arrow, shape-custom, canvas-text, canvas-image, sticky-note

Rectangle:
{ "entityId": "canvas_board", "operation": "insert", "content": { "className": "shape-rectangle", "attrs": { "position": { "x": 0, "y": 0, "width": 200, "height": 150 }, "style": { "fill": "#4A90E2" } } } }

Line (with independent endpoints):
{ "entityId": "canvas_board", "operation": "insert", "content": { "className": "shape-line", "attrs": { "position": { "x": -100, "y": -50, "width": 200, "height": 100 }, "semantic": { "x1": -100, "y1": 0, "x2": 100, "y2": 0 }, "style": { "stroke": "#333333", "strokeWidth": 2 } } } }

Arrow (connector with arrowhead):
{ "entityId": "canvas_board", "operation": "insert", "content": { "className": "shape-arrow", "attrs": { "position": { "x": -150, "y": -75, "width": 300, "height": 150 }, "semantic": { "x1": -150, "y1": -75, "x2": 150, "y2": 75 }, "style": { "stroke": "#E74C3C", "strokeWidth": 3 } } } }

Text (note attrs.text for content, style.color for text color, textAlign defaults to 'center'):
{ "entityId": "canvas_board", "operation": "insert", "content": { "className": "canvas-text", "attrs": { "position": { "x": 0, "y": 0, "width": 300, "height": 50 }, "text": "Hello World", "style": { "fontSize": 24, "color": "#000000", "textAlign": "center" } } } }

Image (AI-generated, auto-fetched):
{ "entityId": "canvas_board", "operation": "insert", "content": { "className": "canvas-image", "attrs": { "position": { "x": -200, "y": -150, "width": 400, "height": 300 }, "semantic": { "imageSource": "grok", "prompt": "Modern abstract art", "alt": "Abstract" } } } }

Custom Vector Shape (SVG Path):
{ "entityId": "canvas_board", "operation": "insert", "content": { "className": "shape-custom", "attrs": { "position": { "x": 0, "y": 0, "width": 200, "height": 200 }, "semantic": { "path": "M75,5 L90,55 L140,55 L100,85 L115,135 L75,105 L35,135 L50,85 L10,55 L60,55 Z" }, "style": { "fill": "#FFD700", "stroke": "#FFA500" } } } }

🎬 PRESENTATIONS → Slides and Elements
────────────────────────────────────────────────────────────
⚠️ IMPORTANT: CREATE SLIDES FIRST, then add elements to them!

Common className values:
- slide (the slide container - create this first!)
- slide-text, slide-title, slide-subtitle (text elements on slides)
- slide-shape (shapes: rectangle, circle, triangle, arrow, line, custom)
- slide-image (images on slides)

STEP 1 - Create a slide:
{ "entityId": "presentation_abc", "operation": "insert", "target": { "at": "end" }, "content": { "className": "slide", "attrs": { "semantic": { "layout": "content", "backgroundColor": "#ffffff" } } } }

STEP 2 - Add text to the slide:
{ "entityId": "presentation_abc", "operation": "insert", "target": { "slideNumber": 1 }, "content": { "className": "slide-text", "attrs": { "position": { "x": 100, "y": 100, "width": 400, "height": 100 }, "text": "Welcome to my presentation", "style": { "fontSize": 32, "textAlign": "center" } } } }

STEP 3 - Add shape to the slide:
{ "entityId": "presentation_abc", "operation": "insert", "target": { "slideNumber": 1 }, "content": { "className": "slide-shape", "attrs": { "position": { "x": 300, "y": 200, "width": 200, "height": 200 }, "semantic": { "shapeType": "circle" }, "style": { "fill": "#50C878", "stroke": "#2E5C8A" } } } }

STEP 4 - Add image to the slide (stock photo, auto-fetched):
{ "entityId": "presentation_abc", "operation": "insert", "target": { "slideNumber": 1 }, "content": { "className": "slide-image", "attrs": { "position": { "x": 50, "y": 50, "width": 300, "height": 200 }, "semantic": { "imageSource": "unsplash", "query": "business presentation", "alt": "Business" } } } }

STEP 5 (ADVANCED) - Add custom vector shape with SVG path:
{ "entityId": "presentation_abc", "operation": "insert", "target": { "slideNumber": 1 }, "content": { "className": "slide-shape", "attrs": { "position": { "x": 400, "y": 100, "width": 150, "height": 150 }, "semantic": { "shapeType": "custom", "path": "M75,5 L90,55 L140,55 L100,85 L115,135 L75,105 L35,135 L50,85 L10,55 L60,55 Z" }, "style": { "fill": "#FFD700", "stroke": "#FFA500" } } } }

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📐 SVG PATH SYNTAX - Universal for Canvas & Presentations
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Custom shape path commands (SVG syntax):
- M x,y = Move to absolute position
- L x,y = Line to absolute position  
- H x = Horizontal line to x
- V y = Vertical line to y
- C x1,y1 x2,y2 x,y = Cubic Bezier curve
- Q x1,y1 x,y = Quadratic Bezier curve
- A rx,ry angle large sweep x,y = Arc
- Z = Close path
- Lowercase commands (m,l,h,v,c,q,a) use relative coordinates

⚠️ CRITICAL PATH COORDINATES:
- Path coordinates are INTERNAL to the shape's bounding box (viewBox)
- Element position.{x,y} places the shape on canvas/slide
- Element position.{width,height} defines the bounding box for the path
- Path coordinates are relative to this bounding box (0,0 = top-left of box)

Example:
  position: { x: 100, y: 100, width: 150, height: 150 }
  path: "M75,5 L75,145 Z"  // Line from (75,5) to (75,145) WITHIN the 150x150 box
                            // The entire box is placed at position (100, 100)

Common custom shapes (use with Canvas shape-custom OR Presentations slide-shape):
- Star: "M75,5 L90,55 L140,55 L100,85 L115,135 L75,105 L35,135 L50,85 L10,55 L60,55 Z"
- Heart: "M100,30 C60,0 20,0 20,40 C20,70 40,90 100,150 C160,90 180,70 180,40 C180,0 140,0 100,30 Z"
- Pentagon: "M100,10 L190,80 L155,180 L45,180 L10,80 Z"
- Hexagon: "M50,10 L150,10 L200,100 L150,190 L50,190 L0,100 Z"

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Slide properties (in attrs.semantic):
- layout: "title" (centered title slide) or "content" (default, normal content slide)
- backgroundColor: "#4A90E2" (hex color for slide background)
- transition: "slide" (default), "fade", "none" (slide transition effect)

Presentation slide dimensions: 800x450 pixels (16:9 aspect ratio)
- Coordinate system: TOP-LEFT ORIGIN (0, 0)
- Valid coordinates: x: 0 to 800, y: 0 to 450
- ⚠️ CRITICAL: Negative coordinates are NOT allowed and will be clamped to 0
- All elements require: position: { x, y, width, height }
- Example: { x: 100, y: 50, width: 300, height: 100 } ✅
- Example: { x: -100, y: 50, width: 300, height: 100 } ❌ Will be clamped to x: 0

Common positions:
- Top-left: x: 50, y: 50
- Top-center: x: 300, y: 50
- Center: x: 300, y: 175
- Bottom: x: 300, y: 350

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🎨 CANVAS APP COORDINATES (Different from Presentations!)
────────────────────────────────────────────────────────────
Canvas uses CENTER ORIGIN (0, 0) with 5000x5000px dimensions
- Origin at CENTER of canvas
- Valid coordinates: x: -2500 to +2500, y: -2500 to +2500
- Negative coordinates ARE allowed (X: negative←left, positive→right, Y: negative↑up, positive↓down)
- Example: { x: -1000, y: -500, width: 300, height: 200 } ✅

🔗 LINES & ARROWS - Endpoint Coordinates
────────────────────────────────────────────────────────────
Lines and arrows use ABSOLUTE coordinates for their endpoints:
- position: { x, y, width, height } = Bounding box (for hit detection/selection)
- semantic: { x1, y1, x2, y2 } = Line start/end points (ABSOLUTE canvas coords)

⚠️ CRITICAL: Line endpoints (x1, y1, x2, y2) are ABSOLUTE canvas coordinates, NOT relative to position!

Example - Horizontal line from left to right:
  position: { x: -100, y: -10, width: 200, height: 20 }  // Bounding box
  semantic: { x1: -100, y1: 0, x2: 100, y2: 0 }          // Start at (-100, 0), end at (100, 0)

Example - Diagonal arrow from top-left to bottom-right:
  position: { x: -200, y: -150, width: 400, height: 300 }
  semantic: { x1: -200, y1: -150, x2: 200, y2: 150 }     // (-200, -150) → (200, 150)

Position calculation helper:
  minX = Math.min(x1, x2)
  maxX = Math.max(x1, x2)
  minY = Math.min(y1, y2)
  maxY = Math.max(y1, y2)
  position = { x: minX, y: minY, width: maxX - minX, height: maxY - minY }

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

**STYLE ATTRIBUTES:** Use numbers for fontSize, fontWeight. Use strings for colors, textAlign.
- fontSize: 16 (number, pixels)
- fontWeight: 600 (number)
- color: "#333" (string, for text elements)
- fill: "#4A90E2" (string, for shapes)
- textAlign: "center" (string)

**TARGET SELECTORS:**
- { "at": "end" } - End of content (Notes)
- { "slideNumber": 1 } - Specific slide (Presentations)
- { "nodeId": "node_123" } - Update/delete specific element
- { "cellRef": "A1" } - Specific cell (Sheets)

**KEY POINTS:**
- 🔑 UNIVERSAL TEXT PATTERN: All text content uses attrs.text (Notes, Canvas text, Presentation text, Playbooks)
- 🔑 UNIVERSAL SVG PATTERN: Custom vector shapes use semantic.path with SVG path data
- All entities use className-based objects (except Sheets which use attrs parameter directly)
- Notes: className "note-paragraph" with attrs.text
- Playbooks: className "playbook-prompt" with attrs.text (system prompts/instructions)
- Canvas: className "shape-custom" with semantic.path for custom vector shapes
- Presentations: className "slide-shape" with semantic.shapeType="custom" and semantic.path
- Canvas/Presentations: Include position for spatial elements, text elements also use attrs.text
- Sheets: Put cell values directly in attrs parameter, not content
- Limits: 1000 words per string, 50 items per array, 100 operations per batch
`,

  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'Entity ID (note_*, sheet_*, canvas_*, presentation_*, or playbook_*).',
        required: true,
        validator: (value: string) => {
          if (!value) return 'Entity ID is required';
          if (value.startsWith('doc_') || value.startsWith('pdf_') || value.startsWith('file_')) {
            return 'entity_write is for Notes, Sheets, Playbooks, and Presentations only. Use doc_write for Documents (doc_*) or PDFs (pdf_*/file_*).';
          }
          if (!value.startsWith('note_') && !value.startsWith('sheet_') && !value.startsWith('presentation_') && !value.startsWith('canvas_') && !value.startsWith('playbook_')) {
            return 'entity_write is for Notes (note_*), Sheets (sheet_*), Canvas (canvas_*), Presentations (presentation_*), and Playbooks (playbook_*) only.';
          }
          return null;
        }
      },
      {
        name: 'operation',
        type: 'string',
        description: 'Operation type: insert | replace | update | delete | batch.',
        required: true,
        enum: ['insert', 'replace', 'update', 'delete', 'batch'],
        validator: (value: string) => {
          const validOps = ['insert', 'replace', 'update', 'delete', 'batch'];
          if (!validOps.includes(value)) {
            return `Invalid operation. Must be one of: ${validOps.join(', ')}`;
          }
          return null;
        }
      },
      {
        name: 'target',
        type: 'object',
        description: 'What to modify. Defaults to { all: true }. See SELECTORS section.',
        required: false,
        validator: async (value: any, params: any) => {
          if (!value) return null;
          const validKeys = ['all', 'nodeId', 'nodeIds', 'className', 'range', 'at', 'before', 'after', 'cellRef', 'slideNumber', 'containerId'];
          const hasValidKey = Object.keys(value).some(key => validKeys.includes(key));
          if (!hasValidKey) {
            return `Invalid target selector. Must include one of: ${validKeys.join(', ')}`;
          }
          
          // Validate slideNumber is a number
          if ('slideNumber' in value && (typeof value.slideNumber !== 'number' || value.slideNumber < 1)) {
            return 'slideNumber must be a positive number (e.g., 1, 2, 3)';
          }
          
          // ✅ NEW: Validate slideNumber exists in presentation
          if ('slideNumber' in value && params.entityId?.startsWith('presentation_')) {
            try {
              const app = await getApplicationForEntity(params.entityId);
              if (app && 'getAllSlides' in app && typeof (app as any).getAllSlides === 'function') {
                const slides = (app as any).getAllSlides();
                const slideCount = slides.length;
                
                if (value.slideNumber > slideCount) {
                  return `Slide ${value.slideNumber} does not exist. This presentation has ${slideCount} slide${slideCount === 1 ? '' : 's'}. Valid slide numbers: 1-${slideCount}. Create more slides first using: { "operation": "insert", "content": { "className": "slide" } }`;
                }
              }
            } catch (error) {
              // If we can't validate (e.g., entity not loaded), let it proceed
              // The handler will catch the error with better context
              console.warn('[entity_write validator] Could not validate slideNumber:', error);
            }
          }
          
          // Validate cellRef format
          if ('cellRef' in value && typeof value.cellRef === 'string') {
            const cellRefPattern = /^[A-Z]+[0-9]+$/;
            if (!cellRefPattern.test(value.cellRef)) {
              return 'cellRef must be in format like "A1", "B2", "AA10" (column letters + row number)';
            }
          }
          
          return null;
        }
      },
      {
        name: 'content',
        type: 'object',
        description: 'Content to insert/replace. Format: (1) Object {className, attrs} for Notes/Canvas/Presentations, (2) For Sheets use attrs parameter instead. See CONTENT FORMAT section.',
        required: false,
        validator: (value: any, params: any) => {
          if (!value) {
            if (params.operation === 'insert' || params.operation === 'replace') {
              return `Content is required for ${params.operation} operations`;
            }
            return null;
          }
          
          // Validate string content (legacy support only - all entities should use objects now)
          if (typeof value === 'string') {
            // Legacy string support: validate word count
            const wordCount = value.trim().split(/\s+/).length;
            if (wordCount > 1000) {
              return `Content exceeds limit of 1000 words (${wordCount} words provided)`;
            }
            // For playbooks, warn about deprecation but allow
            if (params.entityId?.startsWith('playbook_')) {
              console.warn('[entity_write] Legacy string format for playbooks. Use { className: "playbook-prompt", attrs: { text: "..." } } instead.');
            }
            return null;
          }
          
          // Validate structured objects (Canvas/Presentations)
          if (typeof value === 'object' && !Array.isArray(value)) {
            if (!value.className) {
              return 'Structured content must include className. Example: { className: "shape-rectangle", attrs: {...} }';
            }
            if (!value.attrs) {
              return 'Structured content must include attrs object. Example: { className: "shape-rectangle", attrs: { position: {...}, style: {...} } }';
            }
            
            // Validate spatial elements have position
            const spatialClasses = ['shape-', 'slide-', 'canvas-'];
            const needsPosition = spatialClasses.some(prefix => value.className.startsWith(prefix));
            const isContainer = value.className === 'slide' || value.className === 'slide-title';
            
            if (needsPosition && !isContainer && !value.attrs.position) {
              return `${value.className} requires position attribute. Example: { position: { x: 100, y: 100, width: 200, height: 150 } }`;
            }
            
            // Validate images have imageSource
            if (value.className === 'canvas-image' || value.className === 'slide-image') {
              const imageSource = value.attrs.semantic?.imageSource;
              if (!imageSource || (imageSource !== 'grok' && imageSource !== 'unsplash')) {
                return 'Image elements require semantic.imageSource ("grok" or "unsplash")';
              }
              if (imageSource === 'grok' && !value.attrs.semantic?.prompt) {
                return 'Images with imageSource "grok" require semantic.prompt for AI generation';
              }
              if (imageSource === 'unsplash' && !value.attrs.semantic?.query) {
                return 'Images with imageSource "unsplash" require semantic.query for stock photo search';
              }
            }
            
            // Validate Canvas custom shapes have path
            if (value.className === 'shape-custom' && !value.attrs.semantic?.path) {
              return 'shape-custom requires semantic.path attribute with SVG path data. Example: { semantic: { path: "M75,5 L90,55..." } }';
            }
            
            // Validate Canvas lines have endpoint coordinates
            if (value.className === 'shape-line' && !value.attrs.semantic?.x1) {
              return 'shape-line requires semantic with endpoint coordinates: { x1, y1, x2, y2 }. Example: { semantic: { x1: -100, y1: 0, x2: 100, y2: 0 } }';
            }
            
            // Validate Canvas arrows have endpoint coordinates
            if (value.className === 'shape-arrow' && !value.attrs.semantic?.x1) {
              return 'shape-arrow requires semantic with endpoint coordinates: { x1, y1, x2, y2 }. Example: { semantic: { x1: -100, y1: -50, x2: 100, y2: 50 } }';
            }
            
            // Validate line/arrow endpoint coordinates are numbers
            if ((value.className === 'shape-line' || value.className === 'shape-arrow') && value.attrs.semantic) {
              const { x1, y1, x2, y2 } = value.attrs.semantic;
              if (typeof x1 !== 'number' || typeof y1 !== 'number' || typeof x2 !== 'number' || typeof y2 !== 'number') {
                return 'Line/arrow endpoints must be numbers. Example: { semantic: { x1: -100, y1: 0, x2: 100, y2: 0 } }';
              }
              
              // Validate endpoints are within Canvas bounds (-2500 to +2500)
              const coords = [x1, y1, x2, y2];
              const outOfBounds = coords.some(coord => coord < -2500 || coord > 2500);
              if (outOfBounds) {
                return 'Line/arrow endpoints must be within Canvas bounds: -2500 to +2500. Got: {x1: ' + x1 + ', y1: ' + y1 + ', x2: ' + x2 + ', y2: ' + y2 + '}';
              }
            }
            
            // Validate Presentation shapes have shapeType
            if (value.className === 'slide-shape' && !value.attrs.semantic?.shapeType) {
              return 'slide-shape requires semantic.shapeType. Example: { semantic: { shapeType: "rectangle" } }';
            }
            
            // Validate Presentation custom shapes have path
            if (value.className === 'slide-shape' && value.attrs.semantic?.shapeType === 'custom' && !value.attrs.semantic?.path) {
              return 'slide-shape with shapeType="custom" requires semantic.path attribute with SVG path data. Example: { semantic: { shapeType: "custom", path: "M75,5 L90,55..." } }';
            }
            
            // Validate Notes content structure
            if (value.className === 'note-paragraph') {
              if (!value.attrs.text && value.attrs.text !== '') {
                return 'note-paragraph requires attrs.text. Example: { className: "note-paragraph", attrs: { text: "your content here" } }';
              }
            }
            
            // Validate Notes className for note entities
            if (params.entityId?.startsWith('note_') && value.className && !value.className.startsWith('note-')) {
              return `Invalid className "${value.className}" for Notes. Use "note-paragraph" for text content. Example: { className: "note-paragraph", attrs: { text: "..." } }`;
            }
            
            // Validate Playbook content structure
            if (value.className === 'playbook-prompt') {
              if (!value.attrs.text && value.attrs.text !== '') {
                return 'playbook-prompt requires attrs.text. Example: { className: "playbook-prompt", attrs: { text: "your prompt here" } }';
              }
            }
            
            // Validate Playbook className for playbook entities
            if (params.entityId?.startsWith('playbook_') && value.className && value.className !== 'playbook-prompt') {
              return `Invalid className "${value.className}" for Playbooks. Use "playbook-prompt" for prompt content. Example: { className: "playbook-prompt", attrs: { text: "..." } }`;
            }
          }
          
          // Validate arrays
          if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
              if (!value[i].className) {
                return `Array item ${i} missing className`;
              }
              if (!value[i].attrs) {
                return `Array item ${i} missing attrs`;
              }
            }
            if (value.length > 50) {
              return `Content array limited to 50 items (got ${value.length}). Use batch operation for more.`;
            }
          }
          
          return null;
        }
      },
      {
        name: 'attrs',
        type: 'object',
        description: 'Attributes to update. For Sheets: cell references as keys (e.g., { "A1": "value" }). For Notes: node attributes.',
        required: false,
        validator: (value: any, params: any) => {
          if (!value) return null;
          if (params.operation === 'update' && !value) {
            return 'attrs is required for update operations';
          }
          return null;
        }
      },
      {
        name: 'operations',
        type: 'array',
        description: 'Array of operations for batch. Format: [{ op: "insert|replace|update|delete", target: {...}, content: "..." }].',
        required: false,
        validator: (value: any[], params: any) => {
          if (!value) return null;
          if (params.operation === 'batch' && !value) {
            return 'operations is required for batch operation';
          }
          if (Array.isArray(value) && value.length > 100) {
            return `Batch operations limited to 100 (${value.length} provided)`;
          }
          return null;
        }
      }
    ]
  },

  // ✅ NEW: Structured view data for UI display (like doc_write)
  formatViewData: (result: any, toolInput: any) => {
    try {
      if (result?.data) {
        const data = result.data;
        
        return {
          type: 'entity_operation',
          summary: result.message || `${data.operation} completed`,
          data: {
            operation: data.operation,
            nodeCount: data.stats?.nodesModified || 0,
            fullContent: data.messageContext?.fullContent,
            contentPreview: data.messageContext?.contentPreview,
            entityId: toolInput?.entityId,
            affectedNodes: data.affectedNodes
          },
          meta: {
            duration: result.metadata?.executionTime
          }
        };
      }
      
      return {
        type: 'tool_summary',
        summary: result?.message || 'Updated entity',
        data: result?.data || {}
      };
      
    } catch (error) {
      console.warn('[entity_write] Error in formatViewData:', error);
      return {
        type: 'tool_summary',
        summary: 'Updated entity',
        data: {}
      };
    }
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId, operation, target, content, attrs, operations } = params;
    
    console.log('[entity_write] Operation:', operation);
    console.log('[entity_write] Entity:', entityId);
    
    try {
      // 1. Get application instance
      const app = await getApplicationForEntity(entityId);
      
      if (!app) {
        return {
          status: 'error',
          message: 'Entity not found or not a v2 entity',
          error: { code: 'command_error', category: 'execution', details: 'This command only works with v2 entities. Use doc_write for v1 entities.' },
          format: 'plaintext'
        };
      }
      
      // 2. Validate operation
      const validOps = ['insert', 'replace', 'update', 'delete', 'batch'];
      if (!validOps.includes(operation)) {
        return {
          status: 'error',
          message: 'Invalid operation',
          error: { code: 'invalid_operation', category: 'validation' as const, details: `Operation must be one of: ${validOps.join(', ')}` },
          format: 'plaintext'
        };
      }
      
      // 3. Extract content for UI display
      const insertedContent = extractContentFromParams(content, attrs, operation);
      
      // 4. Execute write via BaseApplication
      const result = await app.handleEntityWrite({
        operation,
        target,
        content,
        attrs,
        operations
      });
      
      // 5. Generate user-facing message
      const userMessage = generateContextualMessage(operation, entityId, insertedContent);
      
      // 6. Return result with messageContext for UI
      return {
        status: 'success',
        message: userMessage,
        data: {
          entityId,
          operation,
          affectedNodes: result.affectedNodes || [],
          stats: {
            nodesModified: result.affectedNodes?.length || 0
          },
          // ✅ NEW: Add content for UI display (like doc_write)
          messageContext: {
            fullContent: insertedContent,
            contentPreview: insertedContent && insertedContent.length > 150 
              ? insertedContent.substring(0, 150) + '...' 
              : insertedContent,
            operationSummary: `${operation.charAt(0).toUpperCase()}${operation.slice(1)}ed content`
          }
        },
        format: 'json'
      };
      
    } catch (error: any) {
      console.error('[entity_write] Error:', error);
      
      return {
        status: 'error',
        message: 'Failed to write to entity',
        error: error.message,
        format: 'plaintext'
      };
    }
  }
};

